import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Shield, Megaphone, UserCheck } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "super_admin", label: "Super admin", icon: Shield, desc: "Accès total : partenaires, utilisateurs, paramètres, suppression, export." },
  { value: "marketing", label: "Marketing", icon: Megaphone, desc: "Édition des partenaires, paramètres éditoriaux, validation et export CSV." },
  { value: "moderator", label: "Modérateur", icon: UserCheck, desc: "Validation des soumissions uniquement (approuver / refuser)." },
];

const roleMeta = (r) => ROLES.find((x) => x.value === r) || ROLES[2];

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "moderator", password: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setUsers((await api.get("/admin/users")).data); }
    catch (e) { if (e.response?.status === 403) toast.error("Accès réservé au super admin"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ email: "", name: "", role: "moderator", password: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name, role: u.role, password: "" }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editing.user_id}`, payload);
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/admin/users", form);
        toast.success("Utilisateur créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!confirm(`Supprimer ${u.email} ?`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (me?.role !== "super_admin") {
    return (
      <div className="card-premium p-8 text-center">
        <Shield className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <div className="font-heading font-bold text-navy-900 text-lg">Accès réservé</div>
        <div className="text-muted-foreground text-sm">Seul le super admin peut gérer les utilisateurs.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy-900/60 font-semibold">Utilisateurs</div>
          <h1 className="font-heading font-extrabold text-4xl text-navy-900 tracking-tighter mt-1">Équipe & rôles</h1>
        </div>
        <button onClick={openNew} className="btn-lime text-sm" data-testid="new-user-btn"><Plus className="w-4 h-4" /> Ajouter un utilisateur</button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {ROLES.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.value} className="card-premium p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-navy-900 flex items-center justify-center"><Icon className="w-5 h-5 text-lime-400" /></div>
                <div className="font-heading font-bold text-navy-900">{r.label}</div>
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed">{r.desc}</div>
            </div>
          );
        })}
      </div>

      <div className="card-premium overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Email</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Nom</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Rôle</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Créé le</th>
              <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const meta = roleMeta(u.role);
              const MetaIcon = meta.icon;
              return (
                <tr key={u.user_id} className="border-t border-navy-900/5">
                  <td className="p-4 font-semibold text-navy-900">{u.email} {u.user_id === me?.user_id && <span className="ml-2 chip bg-lime-500/20 text-navy-900">moi</span>}</td>
                  <td className="p-4">{u.name}</td>
                  <td className="p-4">
                    <span className="chip bg-navy-900 text-white inline-flex items-center gap-1.5"><MetaIcon className="w-3 h-3 text-lime-400" /> {meta.label}</span>
                  </td>
                  <td className="p-4 text-muted-foreground">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`} className="p-2 rounded-lg hover:bg-navy-900/5"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(u)} data-testid={`delete-user-${u.user_id}`} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!users.length && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">Aucun utilisateur.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-navy-900/70 mb-1.5 block">Email</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-navy-900/70 mb-1.5 block">Nom</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-navy-900/70 mb-1.5 block">Rôle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-navy-900/70 mb-1.5 block">{editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} placeholder={editing ? "Laisser vide pour ne pas changer" : ""} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(false)} className="btn-ghost text-sm">Annuler</button>
              <button onClick={save} disabled={busy} className="btn-navy text-sm" data-testid="user-save-btn">{busy ? "Enregistrement…" : "Enregistrer"}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
